import { rdSkill } from './rootdata';
import { cgFindId, cgGet, computeVolumeBreakdownFromTickers } from './coingecko';
import { createHealthCollector } from './fetchInstrument';
import { fetchDefiLlama } from './defillama';
import { fetchGithubSignal } from './github';
import { fetchDuneTokenInfo, findOrPrepareDuneDashboard, fetchDuneAnalytics } from './dune';
import { fetchSocialSignals } from './social';
import { fetchWebIntel, buildLightweightDetails } from './ai';
import { fetchComplianceCheck } from './compliance';
import { fetchFirstSnapshot, fetchFirstDocSnapshot } from './wayback';
import { resolveProjectIdentity } from './projectResolve';
import { autoDiscoverProject } from './projectDiscovery';
import { RD_BASE } from './config';
import { resolveSurfProjectName } from './surfNameResolver';
import { fetchEnhancedTeam } from './teamEnhanced';
import { synthesizeRecentUpdates } from './recentUpdates';
import { getCached as getCachedProject } from './projectCache';
import { fetchHomepageContent, synthesizeProjectContext } from './projectContext';
import { getProjectContext } from './projectCache';
import { synthesizeComplianceRisk } from './synthesizeComplianceRisk';
import { factsFromProject } from './synthesizeComplianceRiskCore';
import { validateTokenUnlock } from './validateTokenUnlock';

// ══════════════════════════════════════════════════════
//  全局市场背景（10 分钟内复用缓存）
// ══════════════════════════════════════════════════════
async function fetchMarketContext() {
  try {
    const res = await fetch(`${RD_BASE}/api/market-context`);
    if (!res.ok) return null;
    return res.json();
  } catch { return null; }
}

// 代币期货数据（Binance 资金费率 + OI + Surf 多空比）
async function fetchTokenFutures(symbol) {
  if (!symbol) return null;
  try {
    const res = await fetch(`${RD_BASE}/api/token-futures?symbol=${encodeURIComponent(symbol)}`);
    if (!res.ok) return null;
    return res.json();
  } catch { return null; }
}

// 代币解锁时间表（Surf /token/tokenomics）
// 返回：upcoming unlock events、per-allocation breakdown、30日内解锁总量
// projectName 用于短符号防呆：symbol "H" 会匹配错的项目，
// 传项目名让 proxy 端做交叉校验/fallback。
async function fetchTokenUnlock(symbol, projectName = null) {
  if (!symbol) return null;
  try {
    const params = new URLSearchParams({ symbol });
    if (projectName) params.set('name', projectName);
    const res = await fetch(`${RD_BASE}/api/token-unlock?${params}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.ok) return null;
    return data;
  } catch { return null; }
}

// Artemis 链上基本面指标（perp volume / DAU / fees / revenue / staking 等）
// symbol 传 CoinGecko symbol 或 Artemis 支持的代币简称（如 HYPE、BTC、ETH）
async function fetchArtemisMetrics(symbol) {
  if (!symbol) return null;
  try {
    const res = await fetch(`${RD_BASE}/api/artemis-metrics?symbol=${encodeURIComponent(symbol)}`);
    if (!res.ok) return null;
    const data = await res.json();
    if (!data.ok) return null;
    return data;
  } catch { return null; }
}

// 近期项目新闻（Surf /news/feed，trending + recency 各 5 条）
// 用于 AI 评分 prompt 的新闻语义信号源
//
// 使用 resolveSurfProjectName 多变体探测，避免 "Humanity Protocol"
// 这种 Surf 索引为 "humanity" 的项目漏掉关键新闻（hack/exploit/合作）。
async function fetchSurfNews(projectName) {
  if (!projectName) return null;
  const q = await resolveSurfProjectName(projectName);
  if (!q) return null; // Surf 上确实没这个项目，提前返回
  try {
    const [trRes, rcRes] = await Promise.all([
      fetch(`${RD_BASE}/api/surf?path=/news/feed&project=${encodeURIComponent(q)}&limit=5&sort_by=trending`).catch(() => null),
      fetch(`${RD_BASE}/api/surf?path=/news/feed&project=${encodeURIComponent(q)}&limit=10&sort_by=recency`).catch(() => null),
    ]);
    const tr = trRes?.ok ? await trRes.json() : null;
    const rc = rcRes?.ok ? await rcRes.json() : null;
    const trending = (tr?.data || []).map(n => ({ title: n.title, source: n.source, published_at: n.published_at, url: n.url }));
    const recent   = (rc?.data || []).map(n => ({ title: n.title, source: n.source, published_at: n.published_at, url: n.url }));
    if (!trending.length && !recent.length) return null;
    return { trending, recent, surfProjectName: q };
  } catch { return null; }
}

// 持有者前10浓度（EVM 代币，从 CoinGecko platforms 提取合约地址）
async function fetchTopHolders(cgData) {
  const ethAddr = cgData?.platforms?.ethereum;
  if (!ethAddr) return null;
  try {
    const res = await fetch(`${RD_BASE}/api/surf?path=/token/holders&address=${ethAddr}&chain=ethereum&limit=10`);
    if (!res.ok) return null;
    const data = await res.json();
    const holders = data?.data || [];
    if (!holders.length) return null;
    const top10Pct = holders.slice(0, 10).reduce((sum, h) => sum + (h.percentage || 0), 0);
    return {
      top10Concentration: parseFloat(top10Pct.toFixed(2)),
      holders: holders.slice(0, 10).map(h => ({
        address: h.address,
        percentage: parseFloat((h.percentage || 0).toFixed(3)),
        entityName: h.entity_name || null,
        entityType: h.entity_type || null,
        balance: h.balance ? parseFloat(h.balance) : null,
      })),
      chain: 'ethereum',
    };
  } catch { return null; }
}

// ══════════════════════════════════════════════════════
//  fetchFullProject — 项目全量数据拼接
//  负责编排所有数据源的并行拉取
// ══════════════════════════════════════════════════════
// 名称归一化用于比较
function normName(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

export async function fetchFullProject(nameOrId, { projectId } = {}) {
  let hit;
  let results;
  let matchMethod;
  let resolveInfo = null;

  if (projectId) {
    // ── 有 project_id 时直接用，跳过搜索（最可靠） ──
    hit = { id: projectId, type: 1 };
    results = [];
    matchMethod = "by_id";
  } else {
    // 1. 多源交叉识别（RootData + CoinGecko + DeFiLlama）
    resolveInfo = await resolveProjectIdentity(nameOrId).catch(() => null);
    console.log(`[resolve] 查询「${nameOrId}」候选数:`, resolveInfo?.candidates?.length || 0,
      resolveInfo?.best ? `最佳:${resolveInfo.best.name}(置信度${resolveInfo.best.confidence.toFixed(2)})` : '无');

    // 仍然要做 RootData ser_inv 拿 type=3 团队成员
    // 容错：ser_inv 抖动不应中断整个分析；resolveProjectIdentity 已提供 RootData 兜底，
    // 这里失败时返回空数组，后续 lightweight/AI fallback 仍可继续。
    const searchResults = await rdSkill("ser_inv", { query: nameOrId, precise_x_search: false })
      .catch(e => { console.warn('[ser_inv] failed, continuing without team-member results:', e?.message); return []; });
    results = Array.isArray(searchResults) ? searchResults : (searchResults?.list || searchResults?.data || []);

    // 优先用多源识别结果中的 RootData ID
    const best = resolveInfo?.best;
    if (best && best.confidence >= 0.7 && best.sources.rootdata) {
      hit = { id: best.sources.rootdata.id, type: 1 };
      matchMethod = best.sourceCount >= 2 ? "multi_source" : "exact";
    } else if (best && best.confidence >= 0.5 && best.sources.rootdata) {
      // 中置信度：仍用 RootData 结果但标记为模糊
      hit = { id: best.sources.rootdata.id, type: 1 };
      matchMethod = "substring";
    } else {
      // 低置信度：检查是否短查询词兜底
      const projects = results.filter(x => x.type === 1);
      const qNorm = normName(nameOrId);
      if (qNorm.length <= 3 && projects.length > 0) {
        hit = projects[0];
        matchMethod = "fallback_short_query";
      }
    }
    // 进入 lightweight 前最后一道兜底：RootData ser_inv 里有任何 substring 命中就用
    // 解决：用户搜 "Pharos Network" 但 RootData 实体名只是 "Pharos"，CoinGecko 又能精确
    //       匹配但 best 桶无 rootdata → 之前直接掉到 lightweight，丢掉了 RootData 命中
    if (!hit) {
      const qNorm = normName(nameOrId);
      const projects = results.filter(x => x.type === 1);
      const fuzzy = projects.find(r => {
        const rNorm = normName(r.name);
        if (!rNorm || rNorm.length < 4) return false;
        return rNorm === qNorm || rNorm.includes(qNorm) || qNorm.includes(rNorm);
      });
      if (fuzzy) {
        console.log(`[resolve-fuzzy] RootData 兜底命中: 查询「${nameOrId}」→「${fuzzy.name}」(id=${fuzzy.id})`);
        hit = { id: fuzzy.id, type: 1 };
        matchMethod = "substring";
      }
    }
  }
  // ── 未找到可信匹配 → 轻量分析模式 ──
  if (!hit) {
    console.log(`[lightweight] 未找到「${nameOrId}」的可信匹配，启动轻量分析模式`);
    const lite = await fetchLightweightProject(nameOrId);
    if (resolveInfo) lite.details._resolveInfo = resolveInfo;
    return lite;
  }

  // type=3 是团队成员，取前 4 个（减少 RootData 并发请求数）
  const memberHits = results.filter(x => x.type === 3).slice(0, 4);

  // 2. 并行拉取项目详情 + 融资 + 团队成员详情
  const [details, fundingRaw, ...memberDetails] = await Promise.all([
    rdSkill("get_item", { project_id: hit.id }),
    rdSkill("get_fac", { project_id: hit.id, page: 1, page_size: 20 }).catch(() => null),
    ...memberHits.map(m =>
      rdSkill("get_item", { project_id: m.id }).catch(() => null)
    ),
  ]);

  // ── 关键: fetch 后验证名称匹配 ──
  // 即使 ID 匹配上了，也要确认返回的 details 名称真的对得上查询词
  // 仅在 by_id（从早期雷达/热榜带 ID 进来）时跳过此验证
  if (matchMethod !== "by_id" && matchMethod !== "fallback_short_query") {
    const detailNorm = normName(details.project_name);
    const queryNorm = normName(nameOrId);
    const isOk = detailNorm === queryNorm
      || (detailNorm && (detailNorm.includes(queryNorm) || queryNorm.includes(detailNorm)));
    if (!isOk) {
      console.warn(`[fetch-validate] RootData 返回的项目「${details.project_name}」与查询「${nameOrId}」不匹配，降级到轻量分析模式`);
      return fetchLightweightProject(nameOrId);
    }
  } else if (matchMethod === "by_id") {
    // by_id 路径：用户已显式选了 ID，不强行降级，但仍校验名字一致性 → UI 顶部提示
    const detailNorm = normName(details.project_name);
    const queryNorm = normName(nameOrId);
    const looksMatched = !queryNorm || detailNorm === queryNorm
      || (detailNorm && (detailNorm.includes(queryNorm) || queryNorm.includes(detailNorm)));
    if (!looksMatched) {
      console.warn(`[fetch-validate] by_id 路径：查询「${nameOrId}」拿到「${details.project_name}」(id=${hit.id})，名字不一致 — 已显式选 ID，继续分析但标记 _byIdNameMismatch`);
      details._byIdNameMismatch = { queried: nameOrId, returned: details.project_name, id: hit.id };
    }
  }
  // 附加匹配元信息用于 UI 显示
  details._matchMethod = matchMethod;
  details._matchedId = hit.id;
  if (resolveInfo) details._resolveInfo = resolveInfo;

  // ── 归一化：get_fac 返回 { items: [...] }，ser_inv 返回裸数组 ─
  const fundingRounds = Array.isArray(fundingRaw) ? fundingRaw : (fundingRaw?.items || fundingRaw?.list || fundingRaw?.records || []);

  // 若 get_item 失败（d=null），仍用 ser_inv hit 的浅字段构造 team member，
  // 确保 _rootDataMembers 计数与 RootData 卡片显示的人数一致（避免 teamSize 偏低）
  const teamMembers = memberHits
    .map((h, i) => {
      const d = memberDetails[i];
      if (d) {
        return {
          id: h.id,
          name: h.name,
          rootdataUrl: h.rootdataurl || "",
          twitter: d.social_media?.X || d.social_media?.twitter || h.X || "",
          title: d.title || d.position || "",
          description: (d.description || "").slice(0, 300),
          linkedIn: d.social_media?.linkedin || "",
          logo: d.logo || h.logo || "",
        };
      }
      // fallback: get_item 失败，用 ser_inv hit 的字段
      if (!h?.name) return null;
      return {
        id: h.id,
        name: h.name,
        rootdataUrl: h.rootdataurl || "",
        twitter: h.X || h.twitter || "",
        title: "",
        description: (h.introduce || "").slice(0, 300),
        linkedIn: "",
        logo: h.logo || "",
      };
    })
    .filter(Boolean);
  // 让 fetchEnhancedTeam 的合成层能读到 RootData 团队
  details._rootDataMembers = teamMembers;

  // 3. 所有外部 API 全部并行拉取（去掉重复 fetchTechIntel，fetchWebIntel 内部已调用）
  // Dune 需要 CoinGecko platforms 字段（链上合约地址），故放在 CG IIFE 内部链式调用
  const tokenSymbol = details.token_symbol?.trim() || "";
  const founderNames = memberHits.slice(0, 3).map(m => m.name).filter(Boolean);
  // 数据源健康度收集器 — 每次分析独立，避免并发污染
  const dsh = createHealthCollector();
  const _proj = details.project_name;
  const [[cgData, duneTokenInfo, duneDashboardRaw, duneAnalyticsRaw, topHolders], defiData, webIntel, githubSignal, socialSignals, complianceCheck, waybackSnapshot, docSnapshot, teamSignals, marketContext, tokenFutures, artemisMetrics, tokenUnlock, surfNews, enhancedTeam] = await Promise.all([
    (async () => {
      // NOTE: always return 5-element tuple to avoid destructuring holes
      if (!tokenSymbol) {
        dsh.wrap('coingecko', { project: _proj }, async () => null, () => ({ ok: false, sample: { reason: 'no_token_symbol' } })).catch(() => null);
        return [null, null, null, null, null];
      }
      const id = await dsh.wrap('coingecko-find', { project: _proj, symbol: tokenSymbol }, () => cgFindId(tokenSymbol, details.project_name), r => ({ ok: !!r, sample: { id: r } })).catch(() => null);
      if (!id) return [null, null, null, null, null];
      const cg = await dsh.wrap('coingecko', { project: _proj, id }, () => cgGet(`/coins/${id}?localization=false&tickers=true&community_data=true`), r => ({ ok: !!r, sample: { tickers: r?.tickers?.length, fdv: r?.market_data?.fully_diluted_valuation?.usd, vol24h: r?.market_data?.total_volume?.usd } })).catch(() => null);
      if (!cg) return [null, null, null, null, null];
      // Dune supports EVM chains via dex.trades + Sim API; hyperliquid 是独立 L1
      const EVM_CHAINS_FOR_DUNE = ['ethereum', 'base', 'arbitrum', 'binance-smart-chain', 'polygon-pos', 'optimism'];
      const hasEvmContract = !!(cg.platforms && EVM_CHAINS_FOR_DUNE.some(c => {
        const addr = cg.platforms[c];
        return typeof addr === 'string' && addr.length > 6 && addr !== '0x0000000000000000000000000000000000000000';
      }));
      const isHLNative = !!(cg.platforms?.hyperliquid);
      const isHLSymbol = ['HYPE', 'PURR'].includes((tokenSymbol || '').toUpperCase());
      const [dune, duneDashboard, duneAnalytics, holders] = await Promise.all([
        dsh.wrap('dune-token-info', { project: _proj }, () => fetchDuneTokenInfo(cg.platforms), r => {
          if (!r) {
            // token-info 只支持 EVM 链，HL native 永远不会有
            const reason = isHLNative ? 'hl_native_no_evm' : (hasEvmContract ? 'dune_api_failed' : 'no_evm_contract');
            return { ok: false, sample: { reason } };
          }
          return { ok: true, sample: { priceUsd: r.priceUsd, poolSize: r.poolSize } };
        }).catch(() => null),
        findOrPrepareDuneDashboard(cg.platforms, details.project_name).catch(() => null),
        dsh.wrap('dune-analytics', { project: _proj }, () => fetchDuneAnalytics(cg.platforms, tokenSymbol), r => {
          if (!r) {
            // HL 优先：HL token 走 hl-analytics 专用端点
            let reason;
            if (isHLNative || isHLSymbol) reason = 'hl_native_failed';
            else if (hasEvmContract)      reason = 'dune_api_failed';
            else                          reason = 'no_evm_contract';
            return { ok: false, sample: { reason } };
          }
          return { ok: true, sample: { total_volume: r.total_volume, total_trades: r.total_trades, source: r.source } };
        }).catch(() => null),
        fetchTopHolders(cg).catch(() => null),
      ]);
      return [cg, dune, duneDashboard, duneAnalytics, holders];
    })(),
    dsh.wrap('defillama', { project: _proj, symbol: tokenSymbol }, () => fetchDefiLlama(tokenSymbol, details.project_name), r => ({ ok: !!(r?.tvl || r?.volume || r?.fees), sample: r ? { slug: r.protocol?.slug, type: r.protocol?.type || 'protocol', tvl: r.tvl?.current, volDaily: r.volume?.daily, feeDaily: r.fees?.daily } : null })).catch(() => null),
    dsh.wrap('webintel', { project: _proj }, () => fetchWebIntel(details, memberHits), r => ({ ok: !!r, sample: r ? { hasTech: !!r.techIntel, foundersFromWeb: r.founders?.length || 0 } : null })).catch(() => null),
    dsh.wrap('github', { project: _proj }, () => fetchGithubSignal(details), r => ({ ok: !!r, sample: r ? { commits4w: r.commits4w, stars: r.stars } : null })).catch(() => null),
    dsh.wrap('lunarcrush', { project: _proj, symbol: tokenSymbol }, () => fetchSocialSignals(tokenSymbol), r => ({ ok: !!r, sample: r ? { galaxy_score: r.galaxy_score } : { reason: 'paid_api_402_expected' } })).catch(() => null),
    dsh.wrap('compliance-list', { project: _proj }, () => fetchComplianceCheck(details.project_name, tokenSymbol, founderNames), r => ({ ok: !!r, sample: r ? { ofac: r.ofac, sec: r.sec } : null })).catch(() => null),
    dsh.wrap('wayback-website', { project: _proj, url: details.social_media?.website }, () => fetchFirstSnapshot(details.social_media?.website), r => ({ ok: !!r, sample: r ? { firstSeenAt: r.firstSeenAt } : null })).catch(() => null),
    dsh.wrap('wayback-docs', { project: _proj }, () => fetchFirstDocSnapshot(details.social_media, details.project_name), r => ({ ok: !!r, sample: r ? { firstSeenAt: r.firstSeenAt, source: r.source } : null })).catch(() => null),
    // 团队信号：Twitter 账号年龄 + 路演记录
    (async () => {
      try {
        // 项目主 X handle（提到 handles 数组首位 — Q26 修复 pre-TGE 项目
        // 之前完全错过项目自己的 followers / engagement 数据的盲点）
        const projectXRaw = (details.social_media?.X || '').toString();
        const projectXHandle = projectXRaw
          .replace(/^https?:\/\/(twitter|x)\.com\//i, '')
          .replace(/^@/, '')
          .split(/[/?]/)[0]
          .toLowerCase();

        // 团队成员 Twitter handle
        const teamHandles = teamMembers
          .map(m => (m.twitter || '').replace(/^https?:\/\/(twitter|x)\.com\//, '').replace(/^@/, '').split('/')[0])
          .filter(h => h && h.length > 1)
          .map(h => h.toLowerCase());

        // 项目主账号去重 prepend；team 6 个上限，项目主 1 个（总 7 个）
        const handles = [
          ...(projectXHandle && projectXHandle.length > 1 ? [projectXHandle] : []),
          ...teamHandles.filter(h => h !== projectXHandle).slice(0, 6),
        ];

        // Twitter bios（含 followers_count）+ 推文 engagement，并行拉取
        const twitterAgesPromise = handles.length > 0
          ? fetch(`${RD_BASE}/api/twitter-bios?usernames=${encodeURIComponent(handles.join(','))}`)
              .then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null);

        // 项目主账号的最近 30 条推文（拿 engagement 中位数）
        const projectTweetsPromise = projectXHandle && projectXHandle.length > 1
          ? fetch(`${RD_BASE}/api/twitter-tweets?usernames=${encodeURIComponent(projectXHandle)}&count=30`)
              .then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null);

        // 路演/孵化器记录（对照投资方名单 + Surf 新闻）
        const allInvestors = (fundingRounds || []).flatMap(r =>
          (r.invests || []).map(i => i.name || '')
        ).filter(Boolean);
        const pitchQs = new URLSearchParams({
          project: details.project_name || '',
          investors: allInvestors.slice(0, 20).join(','),
        });
        const pitchPromise = fetch(`${RD_BASE}/api/pitch-records?${pitchQs}`)
          .then(r => r.ok ? r.json() : null).catch(() => null);

        const [twitterResult, projectTweetsResult, pitchResult] = await Promise.all([
          twitterAgesPromise,
          projectTweetsPromise,
          pitchPromise,
        ]);

        // 解析 Twitter 年龄 + followers
        const twitterAges = {};
        if (twitterResult?.bios) {
          Object.entries(twitterResult.bios).forEach(([handle, data]) => {
            const d = typeof data === 'object' ? data : { bio: data };
            if (d.created_at) {
              const ageYears = ((Date.now() - new Date(d.created_at).getTime()) / (365.25 * 24 * 3600 * 1000)).toFixed(1);
              twitterAges[handle.replace(/^@/, '').toLowerCase()] = {
                created_at: d.created_at,
                ageYears: parseFloat(ageYears),
                followers: d.followers || null,
              };
            }
          });
        }

        // 项目主账号社交信号汇总 — 给 scoreProject / verdict / UI 用
        let projectSocial = null;
        if (projectXHandle && twitterAges[projectXHandle]) {
          const proj = twitterAges[projectXHandle];
          // 推文 engagement 中位数（过去 30 条非转发/回复）
          let tweetCount = 0, medLikes = null, medRetweets = null, medReplies = null, medViews = null;
          const tweets = projectTweetsResult?.tweets?.[projectXHandle] || [];
          if (Array.isArray(tweets) && tweets.length > 0) {
            const sortedNums = (key) => tweets.map(t => t?.[key] || 0).sort((a, b) => a - b);
            const med = (arr) => arr.length === 0 ? null : arr[Math.floor(arr.length / 2)];
            tweetCount = tweets.length;
            medLikes    = med(sortedNums('like_count'));
            medRetweets = med(sortedNums('retweet_count'));
            medReplies  = med(sortedNums('reply_count'));
            medViews    = med(sortedNums('impression_count')); // 可能为 0 / null
          }
          // Q31：拿 Telegram 频道用户数（pre-TGE 项目核心社区信号）
          //   来源优先级：details.social_media.telegram > X bio 里 t.me link
          let tgHandle = '';
          const smTg = details?.social_media?.telegram || details?.social_media?.tg || '';
          if (smTg) {
            tgHandle = String(smTg).replace(/^https?:\/\/(t\.me|telegram\.me)\//i, '').replace(/^@/, '').split(/[/?]/)[0];
          }
          if (!tgHandle && twitterResult?.bios?.[projectXHandle]?.bioUrls) {
            const tgUrl = twitterResult.bios[projectXHandle].bioUrls.find(u => /(?:^|\/)t\.me\//i.test(u));
            if (tgUrl) tgHandle = tgUrl.replace(/^https?:\/\/(t\.me|telegram\.me)\//i, '').split(/[/?]/)[0];
          }
          let telegram = null;
          if (tgHandle && tgHandle.length > 1) {
            try {
              const tgRes = await fetch(`${RD_BASE}/api/telegram-stats?handle=${encodeURIComponent(tgHandle)}`)
                .then(r => r.ok ? r.json() : null).catch(() => null);
              if (tgRes?.ok && tgRes.memberCount != null) {
                telegram = {
                  handle: tgRes.handle,
                  memberCount: tgRes.memberCount,
                  type: tgRes.type,
                };
              }
            } catch { /* 静默失败 */ }
          }
          projectSocial = {
            handle: projectXHandle,
            ageYears: proj.ageYears,
            followers: proj.followers,
            tweets30dCount: tweetCount,
            telegram,             // Q31：{ handle, memberCount, type } | null
            medianEngagement: {
              likes:    medLikes,
              retweets: medRetweets,
              replies:  medReplies,
              views:    medViews,
            },
            // 简化的"互动率"指标：中位点赞 / followers
            engagementRate: (proj.followers > 0 && medLikes != null)
              ? +(medLikes / proj.followers * 100).toFixed(3) // % of followers liking median tweet
              : null,
          };
        }

        return {
          twitterAges,      // { handle: { created_at, ageYears, followers } }
          projectSocial,    // 项目主账号汇总（Q26）
          pitchRecord: pitchResult || null,
        };
      } catch { return null; }
    })(),
    // 全局市场背景（恐慌贪婪 + BTC 主导率 + 山寨季信号）
    dsh.wrap('market-context', { project: _proj }, () => fetchMarketContext(), r => ({ ok: !!r, sample: r ? { fearGreed: r.fearGreed, btcDom: r.btcDominance } : null })).catch(() => null),
    // 代币期货数据（Binance 资金费率 + OI + Surf 多空比）
    dsh.wrap('token-futures', { project: _proj, symbol: tokenSymbol }, () => fetchTokenFutures(tokenSymbol), r => {
      if (!tokenSymbol) return { ok: false, sample: { reason: 'no_token_symbol' } };
      if (!r) return { ok: false, sample: { reason: 'proxy_failed' } };
      const hasData = r.fundingRate != null || r.openInterest != null;
      if (!hasData) return { ok: false, sample: { reason: 'no_binance_perp' } };
      return { ok: true, sample: { fundingRate: r.fundingRate, openInterest: r.openInterest, longShortRatio: r.longShortRatio } };
    }).catch(() => null),
    // Artemis 链上基本面（perp volume / DAU / fees / revenue / staking）
    dsh.wrap('artemis', { project: _proj, symbol: tokenSymbol }, () => fetchArtemisMetrics(tokenSymbol), r => ({ ok: !!r?.ok, sample: r?.ok ? { perpVol24h: r.perpVolume24h, source: r.source } : { reason: 'no_data' } })).catch(() => null),
    // 代币解锁时间表（Surf /token/tokenomics，真实链上数据）
    dsh.wrap('surf-unlock', { project: _proj, symbol: tokenSymbol }, () => fetchTokenUnlock(tokenSymbol, details.project_name), r => {
      if (!r?.ok) return { ok: false, sample: { reason: 'no_unlock_data' } };
      const events = Array.isArray(r.events) ? r.events.length : 0;
      const allocs = Array.isArray(r.allocationTotals) ? r.allocationTotals.length : 0;
      return { ok: true, sample: { events, allocs, next30dAmount: r.next30dAmount } };
    }).catch(() => null),
    // 近期项目新闻（Surf /news/feed，喂给 AI 提取语义信号）
    dsh.wrap('surf-news', { project: _proj }, () => fetchSurfNews(details.project_name), r => ({ ok: Array.isArray(r), sample: { count: Array.isArray(r) ? r.length : 0 } })).catch(() => null),
    // 团队增强（Phase 1: Layer A 官网团队页 + Layer C GitHub 贡献者）
    // 与主 Promise.all 并行；内部独立提取 GitHub owner/repo，不阻塞主链路
    dsh.wrap('enhanced-team', { project: _proj }, () => fetchEnhancedTeam(details), r => ({ ok: !!r, sample: r ? { founders: r.founders?.length || 0, contributors: r.topContributors?.length || 0, qualityScore: r.summary?.qualityScore } : null })).catch(() => null),
  ]);

  // ── Hyperliquid 原生链看板后处理 ─────────────────────────────────────────
  // HL 原生代币（无 EVM 合约）无法通过 find-dashboard 获得看板链接。
  // 如果 HL analytics 成功返回了 query_url，用它生成激活链接。
  const isHLNative = duneAnalyticsRaw?.source === 'hyperliquid_market_data';
  const duneDashboard = (
    isHLNative &&
    duneAnalyticsRaw?.query_url &&
    (!duneDashboardRaw || duneDashboardRaw.type === 'none')
  )
    ? { type: 'activate', activateUrl: duneAnalyticsRaw.query_url, chain: 'hyperliquid', coin: duneAnalyticsRaw.coin }
    : duneDashboardRaw;

  const duneAnalytics = duneAnalyticsRaw;

  // ── 项目生命周期时间线 ──────────────────────────────────
  const timeline = [];
  if (details.establishment_date)
    timeline.push({ key: 'founded', label: '项目成立', labelEn: 'Founded', date: details.establishment_date, source: 'RootData' });
  if (githubSignal?.repoCreatedAt)
    timeline.push({ key: 'github', label: 'GitHub 创建', labelEn: 'GitHub Created', date: githubSignal.repoCreatedAt.slice(0, 10), source: 'GitHub' });
  if (githubSignal?.firstReleaseAt)
    timeline.push({ key: 'firstRelease', label: `首次 Release (${githubSignal.firstReleaseTag || ''})`, labelEn: `First Release (${githubSignal.firstReleaseTag || ''})`, date: githubSignal.firstReleaseAt.slice(0, 10), source: 'GitHub' });
  if (waybackSnapshot?.firstSnapshotDate)
    timeline.push({ key: 'wayback', label: '首次网页快照', labelEn: 'First Web Archive', date: waybackSnapshot.firstSnapshotDate, source: 'Wayback Machine', url: waybackSnapshot.firstSnapshotUrl });
  if (docSnapshot?.firstDocDate)
    timeline.push({
      key: 'firstDoc', label: '首次文档公开', labelEn: 'First Docs Published',
      date: docSnapshot.firstDocDate,
      source: docSnapshot.sourceLabel || 'Wayback Machine',
      sourceType: docSnapshot.sourceType || 'wayback',
      url: docSnapshot.firstDocUrl,
    });
  // 最早融资日期
  // RootData get_fac 返回 date（Unix秒）或 published_time（"2024-02-16" 字符串）
  if (fundingRounds?.length > 0) {
    const fundDates = fundingRounds.map(r => {
      if (r.published_time) return r.published_time;
      if (r.date && typeof r.date === 'number') return new Date(r.date * 1000).toISOString().slice(0, 10);
      if (r.date && typeof r.date === 'string') return r.date.slice(0, 10);
      if (r.announced_date) return r.announced_date;
      return null;
    }).filter(Boolean).sort();
    if (fundDates.length > 0) {
      const lastRound = fundingRounds[0]; // get_fac 默认最新在前
      const roundName = lastRound?.rounds || '';
      timeline.push({ key: 'firstFunding', label: `首次融资${roundName ? ' (' + roundName + ')' : ''}`, labelEn: `First Funding${roundName ? ' (' + roundName + ')' : ''}`, date: fundDates[0], source: 'RootData' });
    }
  }
  // 按日期排序
  timeline.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  // ── Q49 cache fallback：本次 6 层全空时，沿用上次成功的 enhancedTeam
  //    Hyperliquid 等匿名项目 RootData 不稳定（4→2→0 在 3 次跑里波动）。
  //    现行为：current.founders=0 + cached.founders>0 → 用 cached + _fromCache 标注。
  //    优点：评分稳定；用户不会看到 team 维度无规律跳变。
  //    缺点：真团队变化（创始人离职）需手动 7 日 TTL 自然过期。
  let finalEnhancedTeam = enhancedTeam || null;
  try {
    const currentEmpty = !(enhancedTeam?.founders?.length) && !(enhancedTeam?.topContributors?.length);
    if (currentEmpty) {
      const prior = await getCachedProject(details?.project_name || '').catch(() => null);
      const priorTeam = prior?.raw_data?.enhancedTeam;
      if (priorTeam?.founders?.length > 0) {
        finalEnhancedTeam = {
          ...priorTeam,
          _fromCache: true,
          _cacheSourceDate: prior?.scored_at || null,
        };
        // 也保留新的 companyStats 如果本次拿到了（Q30 LinkedIn）
        if (enhancedTeam?.companyStats) finalEnhancedTeam.companyStats = enhancedTeam.companyStats;
        console.log(`[team-cache-fallback] "${details?.project_name}" 本次 6 层全空，沿用 ${prior?.scored_at?.slice(0, 10)} 缓存团队 (${priorTeam.founders.length} 人)`);
      }
    }
  } catch (e) {
    console.warn('[team-cache-fallback] 失败:', e.message);
  }

  return {
    details,
    fundingRounds,
    teamMembers,
    cgData,
    defiData,
    webIntel,          // fetchWebIntel 内部已包含 techIntel
    githubSignal,
    socialSignals,
    duneTokenInfo,
    duneDashboard,
    duneAnalytics,    // 30天 DEX 交易量/笔数/独立交易者（需 DUNE_API_KEY）
    complianceCheck,  // OFAC / SEC / FCA / FinCEN MSB 查询结果
    waybackSnapshot,
    timeline,
    teamSignals: teamSignals || null,  // Twitter 年龄 + 路演记录
    marketContext: marketContext || null,  // 全局市场背景（恐慌贪婪 + BTC主导率 + 山寨季）
    tokenFutures: tokenFutures || null,   // 代币期货（资金费率 + OI + 多空比）
    topHolders: topHolders || null,       // 持有者前10浓度（EVM 代币）
    artemisMetrics: artemisMetrics || null, // Artemis 链上基本面（perp volume / DAU / fees / revenue）
    tokenUnlock:    validateTokenUnlock(tokenUnlock, cgData) || null, // 代币解锁（Surf）— 跟 CG 交叉验证避免 symbol 冲突污染
    surfNews:       surfNews       || null, // 近期项目新闻（trending + recency）
    enhancedTeam:   finalEnhancedTeam, // Q49: 6 层全空时沿用 cache
    marketVolume:   computeVolumeBreakdownFromTickers(cgData?.tickers) || null, // 全市场 24h 交易（CEX/DEX 占比 + Binance 单列 + top 5）
    _debug:         { dataSourceHealth: dsh.snapshot() }, // 14+ 个数据源的 ok/ms/sample/error 快照，详情页 footer 🔍 按钮展开
    // Step 1: 项目近况合成 + Step 2: 官网首页抓取（并行，节省总时间）
    ...await (async () => {
      const [recentUpdatesSummary, homepageContent] = await Promise.all([
        synthesizeRecentUpdates(details.project_name, surfNews).catch(() => null),
        fetchHomepageContent(details?.social_media?.website).catch(() => null),
      ]);
      // Step 2c: projectContext 综合合成（依赖前面所有数据）
      // 先查 Supabase 缓存（30d TTL），命中则省 1 次 Claude 调用 (~$0.02-0.05)
      const cached = await getProjectContext(details.project_name).catch(() => null);
      const projectContext = cached || await synthesizeProjectContext({
        details,
        recentUpdatesSummary,
        githubSignal,
        enhancedTeam,
        surfNews,
        homepageContent,
        defiData,
        webIntel,
      }).catch(() => null);
      // Step 2d: A3 — 合规风险合成（projectContext 之后；失败不阻塞，仅置 null）
      const complianceRisk = projectContext
        ? await synthesizeComplianceRisk(
            factsFromProject({ details, projectContext, defiData }),
            { onlyJurisdictions: ['hk', 'eu'] }
          ).catch(() => null)
        : null;
      if (complianceRisk?._error) {
        console.warn(`[compliance] synthesis failed for ${details.project_name}:`, complianceRisk._error,
          '— topics_considered:', complianceRisk.topics_considered?.length || 0);
      } else if (complianceRisk?.findings) {
        console.log(`[compliance] ${details.project_name}: ${complianceRisk.findings.length} findings`,
          `(${complianceRisk.invalid_findings?.length || 0} invalid)`);
      }
      return { recentUpdatesSummary, projectContext, complianceRisk };
    })(),
  };
}

// ══════════════════════════════════════════════════════
//  轻量分析模式 — RootData 搜不到时的兜底路径
//  用 Claude 知识库构建基本信息，再走 CoinGecko/DeFiLlama 等公开数据
// ══════════════════════════════════════════════════════
async function fetchLightweightProject(projectName) {
  const proxyBase = typeof window === 'undefined' ? '' : (RD_BASE || '');

  // 1. 先用 Claude 生成基本信息（拿到可能的 Twitter handle）
  const details = await buildLightweightDetails(projectName);
  const claudeTwitter = details.social_media?.X || "";

  // 2. 自动发现真实 URL（5 路并行 + Twitter Bio 抓取）
  const discovered = await autoDiscoverProject(projectName, {
    proxyBase,
    twitterHandle: claudeTwitter,
  }).catch(() => null);

  // 3. 合并自动发现的 URL（仅在 Claude 没给出时填充，不覆盖 Claude 的）
  if (discovered) {
    if (!details.social_media) details.social_media = {};
    if (!details.social_media.website && discovered.website) {
      details.social_media.website = discovered.website;
      details._discovered_website_source = discovered.sources.website;
    }
    if (!details.social_media.github && discovered.github) {
      details.social_media.github = discovered.github;
    }
    if (!details.social_media.X && discovered.twitter) {
      details.social_media.X = discovered.twitter;
    }
    details._autoDiscovery = {
      website: discovered.sources.website || null,
      github: discovered.sources.github || null,
      twitter: discovered.sources.twitter || null,
      confidence: discovered.confidence,
      debug: discovered._debug,
    };
    console.log(`[lightweight] 自动发现:`, details._autoDiscovery, '→ website=', details.social_media.website, 'github=', details.social_media.github);
  }

  // 3. 并行拉取所有不依赖 RootData 的数据源
  const tokenSymbol = details.token_symbol?.trim() || "";
  const website = details.social_media?.website || "";

  const [[cgData, duneTokenInfo, duneDashboardRaw, duneAnalyticsRaw, topHolders], defiData, webIntel, githubSignal, socialSignals, waybackSnapshot, docSnapshot, marketContext, tokenFutures, artemisMetrics, tokenUnlock, surfNews, enhancedTeam] = await Promise.all([
    (async () => {
      if (!tokenSymbol) return [null, null, null, null, null];
      const id = await cgFindId(tokenSymbol, details.project_name).catch(() => null);
      if (!id) return [null, null, null, null, null];
      const cg = await cgGet(`/coins/${id}?localization=false&tickers=true&community_data=true`).catch(() => null);
      if (!cg) return [null, null, null, null, null];
      const [dune, duneDashboard, duneAnalytics, holders] = await Promise.all([
        fetchDuneTokenInfo(cg.platforms).catch(() => null),
        findOrPrepareDuneDashboard(cg.platforms, details.project_name).catch(() => null),
        fetchDuneAnalytics(cg.platforms, tokenSymbol).catch(() => null),
        fetchTopHolders(cg).catch(() => null),
      ]);
      return [cg, dune, duneDashboard, duneAnalytics, holders];
    })(),
    fetchDefiLlama(tokenSymbol, details.project_name).catch(() => null),
    fetchWebIntel(details, []).catch(() => null),  // 空人员列表，跳过 RootData 团队搜索
    fetchGithubSignal(details).catch(() => null),
    fetchSocialSignals(tokenSymbol).catch(() => null),
    fetchFirstSnapshot(website).catch(() => null),
    fetchFirstDocSnapshot(details.social_media, details.project_name).catch(() => null),
    fetchMarketContext().catch(() => null),
    fetchTokenFutures(tokenSymbol).catch(() => null),
    // Artemis 链上基本面（perp volume / DAU / fees / revenue / staking）
    fetchArtemisMetrics(tokenSymbol).catch(() => null),
    // 代币解锁时间表（Surf /token/tokenomics）
    fetchTokenUnlock(tokenSymbol, details.project_name).catch(() => null),
    // 近期项目新闻（喂给 AI）
    fetchSurfNews(details.project_name).catch(() => null),
    // 团队增强（Phase 1: Layer A + Layer C）
    fetchEnhancedTeam(details).catch(() => null),
  ]);

  const isHLNative = duneAnalyticsRaw?.source === 'hyperliquid_market_data';
  const duneDashboard = (isHLNative && duneAnalyticsRaw?.query_url && (!duneDashboardRaw || duneDashboardRaw.type === 'none'))
    ? { type: 'activate', activateUrl: duneAnalyticsRaw.query_url, chain: 'hyperliquid', coin: duneAnalyticsRaw.coin }
    : duneDashboardRaw;

  // 3. 时间线（轻量模式：AI 知识 + GitHub + Wayback）
  const timeline = [];
  // 归一化日期：YYYY → YYYY-01, YYYY-MM → YYYY-MM-01
  const normDate = (s) => {
    if (!s) return null;
    if (/^\d{4}$/.test(s)) return `${s}-01-01`;
    if (/^\d{4}-\d{2}$/.test(s)) return `${s}-01`;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    return null;
  };
  // 成立日期（来自 Claude 知识）
  const estDate = normDate(details.establishment_date);
  if (estDate)
    timeline.push({ key: 'founded', label: '项目成立', labelEn: 'Founded', date: estDate, source: 'AI 知识库' });
  // 首次公开发布（来自 Claude 知识）
  const annDate = normDate(details.first_announcement_date);
  if (annDate && annDate !== estDate)
    timeline.push({ key: 'firstAnnounce', label: '首次公开发布', labelEn: 'First Announcement', date: annDate, source: 'AI 知识库' });
  if (githubSignal?.repoCreatedAt)
    timeline.push({ key: 'github', label: 'GitHub 创建', labelEn: 'GitHub Created', date: githubSignal.repoCreatedAt.slice(0, 10), source: 'GitHub' });
  if (githubSignal?.firstReleaseAt)
    timeline.push({ key: 'firstRelease', label: `首次 Release (${githubSignal.firstReleaseTag || ''})`, labelEn: `First Release (${githubSignal.firstReleaseTag || ''})`, date: githubSignal.firstReleaseAt.slice(0, 10), source: 'GitHub' });
  if (waybackSnapshot?.firstSnapshotDate)
    timeline.push({ key: 'wayback', label: '首次网页快照', labelEn: 'First Web Archive', date: waybackSnapshot.firstSnapshotDate, source: 'Wayback Machine', url: waybackSnapshot.firstSnapshotUrl });
  if (docSnapshot?.firstDocDate)
    timeline.push({
      key: 'firstDoc', label: '首次文档公开', labelEn: 'First Docs Published',
      date: docSnapshot.firstDocDate,
      source: docSnapshot.sourceLabel || 'Wayback Machine',
      sourceType: docSnapshot.sourceType || 'wayback',
      url: docSnapshot.firstDocUrl,
    });
  // 融资历史（来自 Claude 知识，格式 "YYYY-MM 轮次 金额; YYYY-MM ..."）
  if (details.funding_history && typeof details.funding_history === 'string') {
    const entries = details.funding_history.split(/[;；]/).map(s => s.trim()).filter(Boolean);
    entries.forEach((entry, i) => {
      const dateMatch = entry.match(/(\d{4}(?:-\d{2})?(?:-\d{2})?)/);
      if (dateMatch) {
        const d = normDate(dateMatch[1]);
        if (d) timeline.push({
          key: `aiFunding_${i}`,
          label: `融资: ${entry.replace(dateMatch[1], '').trim()}`,
          labelEn: `Funding: ${entry.replace(dateMatch[1], '').trim()}`,
          date: d,
          source: 'AI 知识库',
        });
      }
    });
  }
  timeline.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  // ── Q49 cache fallback（lightweight 流程，同主流程逻辑）
  let finalEnhancedTeamLW = enhancedTeam || null;
  try {
    const currentEmpty = !(enhancedTeam?.founders?.length) && !(enhancedTeam?.topContributors?.length);
    if (currentEmpty) {
      const prior = await getCachedProject(details?.project_name || '').catch(() => null);
      const priorTeam = prior?.raw_data?.enhancedTeam;
      if (priorTeam?.founders?.length > 0) {
        finalEnhancedTeamLW = {
          ...priorTeam,
          _fromCache: true,
          _cacheSourceDate: prior?.scored_at || null,
        };
        if (enhancedTeam?.companyStats) finalEnhancedTeamLW.companyStats = enhancedTeam.companyStats;
        console.log(`[team-cache-fallback] "${details?.project_name}" (lightweight) 沿用 ${prior?.scored_at?.slice(0, 10)} 缓存团队 (${priorTeam.founders.length} 人)`);
      }
    }
  } catch (e) {
    console.warn('[team-cache-fallback] lightweight 失败:', e.message);
  }

  return {
    details,
    fundingRounds: [],
    teamMembers: [],
    cgData,
    defiData,
    webIntel,
    githubSignal,
    socialSignals,
    duneTokenInfo,
    duneDashboard,
    duneAnalytics: duneAnalyticsRaw,
    complianceCheck: null,
    waybackSnapshot,
    timeline,
    marketContext: marketContext || null,
    tokenFutures: tokenFutures || null,
    topHolders: topHolders || null,
    artemisMetrics: artemisMetrics || null,
    tokenUnlock:    tokenUnlock    || null,
    surfNews:       surfNews       || null,
    enhancedTeam:   finalEnhancedTeamLW, // Q49 cache fallback
    marketVolume:   computeVolumeBreakdownFromTickers(cgData?.tickers) || null,
    // Step 1 + Step 2 合成（并行 stage 后 projectContext sequential）
    ...await (async () => {
      const [recentUpdatesSummary, homepageContent] = await Promise.all([
        synthesizeRecentUpdates(details.project_name, surfNews).catch(() => null),
        fetchHomepageContent(details?.social_media?.website).catch(() => null),
      ]);
      // 同主路径：先查 30d Supabase 缓存
      const cached = await getProjectContext(details.project_name).catch(() => null);
      const projectContext = cached || await synthesizeProjectContext({
        details,
        recentUpdatesSummary,
        githubSignal,
        enhancedTeam,
        surfNews,
        homepageContent,
        defiData,
        webIntel,
      }).catch(() => null);
      const complianceRisk = projectContext
        ? await synthesizeComplianceRisk(
            factsFromProject({ details, projectContext, defiData }),
            { onlyJurisdictions: ['hk', 'eu'] }
          ).catch(() => null)
        : null;
      return { recentUpdatesSummary, projectContext, complianceRisk };
    })(),
  };
}
